import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const DEFAULT_THRESHOLD = 180; // 3 minutes

export const useSuspiciousThreshold = () => {
  const queryClient = useQueryClient();

  const { data: thresholdSeconds = DEFAULT_THRESHOLD } = useQuery({
    queryKey: ['suspicious-threshold'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'suspicious_threshold_seconds')
        .maybeSingle();

      if (error || !data) return DEFAULT_THRESHOLD;

      const parsed = typeof data.value === 'number'
        ? data.value
        : parseInt(String(data.value), 10);

      return !isNaN(parsed) && parsed > 0 ? parsed : DEFAULT_THRESHOLD;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { mutate: setThresholdSeconds } = useMutation({
    mutationFn: async (seconds: number) => {
      const clamped = Math.max(30, Math.min(720, seconds));
      const { error } = await supabase
        .from('system_settings')
        .update({ value: clamped as any, updated_at: new Date().toISOString() })
        .eq('key', 'suspicious_threshold_seconds');

      if (error) throw error;
      return clamped;
    },
    onSuccess: (clamped) => {
      queryClient.setQueryData(['suspicious-threshold'], clamped);
    },
  });

  return { thresholdSeconds, setThresholdSeconds };
};
